import { useActiveAds, useAdImpression, useAdClick } from "@/hooks/useAds";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, Sparkles, Megaphone } from "lucide-react";
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { SiteAd } from "./SiteAd";

interface SidebarAdProps {
  className?: string;
  maxAds?: number;
}

export const SidebarAd = ({ className = "", maxAds = 3 }: SidebarAdProps) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const { data: ads, isLoading } = useActiveAds('sidebar');
  const handleAdClick = useAdClick();
  const [currentAdIndex, setCurrentAdIndex] = useState(0);

  const displayAds = ads?.slice(0, maxAds) || [];
  
  // إجمالي العناصر = الإعلانات + المساحة للإيجار
  const totalItems = displayAds.length + 1;
  const isShowingPlaceholder = currentAdIndex >= displayAds.length;
  const currentAd = !isShowingPlaceholder ? displayAds[currentAdIndex] : null;
  
  // Track impression when ad is displayed
  useAdImpression(currentAd?.id);

  // Rotate ads every 8 seconds (including placeholder)
  useEffect(() => {
    if (!isLoading) {
      const interval = setInterval(() => {
        setCurrentAdIndex((prev) => (prev + 1) % totalItems);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [totalItems, isLoading]);

  const handlePlaceholderClick = () => {
    navigate('/advertise');
  };

  if (isLoading) return <SiteAd className={className} />;

  // إذا كان الدور على المساحة للإيجار
  if (isShowingPlaceholder) {
    return (
      <div className="space-y-4">
        <Card className={`overflow-hidden bg-gradient-to-br from-primary via-emerald-600 to-primary border-primary/30 shadow-xl ${className}`}>
          <CardContent className="p-0">
            <div
              className="cursor-pointer group p-5"
              onClick={handlePlaceholderClick}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              {/* Badge */}
              <div className="flex items-center justify-between mb-4">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/90 bg-white/20 px-2 py-1 rounded-full">
                  <Sparkles className="w-3 h-3" />
                  {isRTL ? 'لمدة محدودة' : 'Limited Time'}
                </span>
              </div>

              {/* Icon */}
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                <Megaphone className="w-8 h-8 text-white" />
              </div>

              {/* Content */}
              <div className="text-center space-y-3">
                <h3 className="font-bold text-white text-lg">
                  {isRTL ? 'مساحة إعلانية للإيجار' : 'Ad Space For Rent'}
                </h3>
                
                <p className="text-sm text-white/80">
                  {isRTL ? 'اعرض خدماتك أمام آلاف الزوار' : 'Showcase your services to thousands'}
                </p>

                {/* Price */}
                <div className="bg-white/20 backdrop-blur-sm rounded-xl py-3 px-4">
                  <div className="text-3xl font-bold text-white">300</div>
                  <div className="text-sm text-white/80">{isRTL ? 'ريال / شهر' : 'SAR / month'}</div>
                </div>

                <Button 
                  variant="secondary" 
                  size="sm"
                  className="w-full bg-white text-primary hover:bg-white/90 font-bold shadow-lg group-hover:shadow-xl transition-shadow"
                >
                  {isRTL ? 'احجز مساحتك' : 'Book Your Space'}
                  <ExternalLink className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        <SiteAd />
      </div>
    );
  }

  const title = isRTL && currentAd?.title_ar ? currentAd.title_ar : currentAd?.title;
  const description = isRTL && currentAd?.description_ar ? currentAd.description_ar : currentAd?.description;

  return (
    <div className="space-y-4">
      <Card className={`overflow-hidden bg-gradient-to-br from-card via-card to-accent/5 border-primary/20 ${className}`}>
        <CardContent className="p-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentAd?.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="cursor-pointer group"
              onClick={() => handleAdClick(currentAd!.id, currentAd!.link_url)}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              {/* Ad Badge */}
              <div className="px-4 pt-3 flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  <Sparkles className="w-3 h-3" />
                  {isRTL ? 'مميز' : 'Sponsored'}
                </span>
                {totalItems > 1 && (
                  <div className="flex gap-1">
                    {Array.from({ length: totalItems }).map((_, index) => (
                      <button
                        key={index}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${
                          index === currentAdIndex ? 'bg-primary' : 'bg-primary/30'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentAdIndex(index);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Ad Image */}
              {currentAd?.image_url && (
                <div className="px-4 pt-3">
                  <div className="relative aspect-video rounded-lg overflow-hidden shadow-lg">
                    <img 
                      src={currentAd.image_url} 
                      alt={title || 'Advertisement'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              )}

              {/* Ad Content */}
              <div className="p-4 space-y-3">
                {title && (
                  <h3 className="font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {title}
                  </h3>
                )}
                
                {description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {description}
                  </p>
                )}

                <Button 
                  variant="default" 
                  size="sm"
                  className="w-full group-hover:shadow-lg transition-shadow"
                >
                  {isRTL ? 'اكتشف المزيد' : 'Discover More'}
                  <ExternalLink className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </CardContent>
      </Card>
      <SiteAd />
    </div>
  );
};
